import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import Seo from '~components/Seo'
import DataViewer from '~utils/DataViewer'
import PageHeader from '~components/PageHeader'
import styled from '@emotion/styled'
import { useQueryParam, DelimitedArrayParam, withDefault } from 'use-query-params'
import client from '~utils/sanityClient'
import Section from '~components/Section'
import Tile from '~components/Tile'
import Button from '~components/Button'
import { mobile, tablet } from '~styles/global'
import { ChevronDown } from '~components/Svg'
import useBreakpoint from '~utils/useBreakpoint'
import SlideDown from 'react-slidedown'
import 'react-slidedown/lib/slidedown.css'

const MobileSlideDown = ({ children }) => {
	const { isMobile } = useBreakpoint()
	return (
		isMobile ? <StyledSlideDown>{children}</StyledSlideDown> : children
	)
}

const PostIndex = ({ data }) => {

	const { page } = data
	const [posts, setPosts] = useState([])
	const [catsOpen, setCatsOpen] = useState(false)
	const [tagsOpen, setTagsOpen] = useState(false)
	const { isMobile } = useBreakpoint()

	const removeEntry = (array, entry) => {
		let newArr = array
		newArr = newArr.filter(e => e !== entry)
		return newArr
	}

	const [tags, setTags] = useQueryParam('tags', withDefault(DelimitedArrayParam, []))
	const [categories, setCategories] = useQueryParam('categories', withDefault(DelimitedArrayParam, []))

	// const [categories, setCategories] = useState([])

	useEffect(() => {
		let query = generateQuery()
		client.fetch(query).then(result => {
			setPosts(result)
			// if(result?.length){
				
			// }
		})
	}, [tags, categories])

	const generateQuery = () => {

		let query = '*['

		if (data.page?.title === 'Articles') {
			query += '_type == "article" '
		} else if (data.page?.title === 'Stories'){
			query += '_type == "story" '
		} else {
			query += '_type == "volunteerStory" '
		}

		// category or tag
		// if (categories.length) {
		// 	categories.map((category, i)=> (
		// 		query += ` ${i === 1 ? '||' : '&&'} category->slug.current == '${category}' `
		// 	))
		// }

		if (categories.length){
			query += `&& count((categories[]->slug.current)[@ in [${categories.map(cat => '"' + cat + '"')}]]) > 0`
		}
		if (tags.length){
			query += `&& count((tags[]->slug.current)[@ in [${tags.map(tag => '"' + tag + '"')}]]) > 0`
		}
		
		query += ']'

		// add order
		query += '| order(_createdAt desc)'

		query += `{
				_id,
				title,
				_type,
				header,
				'headerImage': header.image->{id},
				slug
			}`

		// console.log(query)
		return query
	}

	return(
		<>
			<Seo 
				title={page?.title}
				metaTitle={page?.seo?.metaTitle}
				description={page?.seo?.metaDescription}
				image={page?.seo?.socialImage}
			/>
			<PageHeader content={page?.header} page={page}/>
			<Filters>
				<MobileFilterHeader dark={catsOpen}
					className='h5'
					onClick={() => setCatsOpen(!catsOpen)}
				>
					<MobileFilterHeading>
						{categories.length === 0 ? 'All Catgeories' : `Categories ${categories.length}`}
					</MobileFilterHeading>
					<ArrowDown rotate={catsOpen}/>
				</MobileFilterHeader>
				<MobileSlideDown>
					{ (catsOpen || !isMobile) &&
						<CategoryMenu>
							<Container>
								<AllCategories 
									underline={categories.length === 0} 
									onClick={() => setCategories([])}
									className='h5'
								>All Categories</AllCategories>
								{page?.categoryMenu.map(category => (
									<CategoryButton 
										key={category.slug?.current} 
										onClick={() => categories.includes(category.slug?.current) 
											? setCategories(removeEntry(categories, category.slug?.current)) 
											: setCategories([...categories, category.slug?.current])
										}
										underline={categories.includes(category.slug?.current)}
										className='h5'
									>
										{category.title}
									</CategoryButton>
								))}
							</Container>
						</CategoryMenu>
					}
				</MobileSlideDown>
				<MobileFilterHeader 
					className='h5'
					dark={tagsOpen}
					onClick={() => setTagsOpen(!tagsOpen)}
				>
					<MobileFilterHeading>
						{tags.length === 0 ? 'All Tags' : `Tags ${tags.length}`}
					</MobileFilterHeading>
					<ArrowDown rotate={tagsOpen}/>
				</MobileFilterHeader>
				<MobileSlideDown>
					{ (tagsOpen || !isMobile) &&
						<TagMenu>
							<Container>
								<TagButton 
									underline={tags.length === 0} 
									onClick={() => setTags([])}
									className='h5'
								>
								All Tags
								</TagButton>
								{page?.tagMenu.map(tag => (
									<TagButton key={tag.slug?.current} 
										onClick={() => tags.includes(tag.slug?.current) 
											? setTags(removeEntry(tags, tag.slug?.current)) 
											: setTags([...tags, tag.slug?.current])
										}
										underline={tags.includes(tag.slug?.current)}
										className='h5'
									>
										{tag.title}
									</TagButton>
								))}
							</Container>
						</TagMenu>
					}
				</MobileSlideDown>
			</Filters>
			
			<Posts>
				{posts.map(post => (
					<Post key={post._id} content={post}/>
				))}
				{posts.length === 0 && <NoPosts>No results</NoPosts>}
			</Posts>
			<DataViewer data={page} name="page" />
		</>
	)
}

const Container = styled.div`
	grid-column: span 12;
`
const Filters = styled.div`
	padding: 100px 0 var(--l); 
	${mobile}{
		padding: 0;
		background-color: var(--dteal60);
		color: var(--white);
		margin-bottom: var(--l);
	}
`
const FilterMenu = styled(Section)`
	${mobile}{
		background-color: var(--dteal);
		margin: 0 -30px; 
		padding: 0 30px var(--s);
	}
`
const MobileFilterHeader = styled.button`
	display: none;
	width: 100%;
	padding: var(--l) 30px;
	justify-content: space-between;
	align-items: center;
	background-color: ${props => props.dark ? 'var(--dteal)' : 'transparent'};
	transition: background-color 0.2s;
	${mobile}{
		display: flex;
	}
`
const MobileFilterHeading = styled.h5`
	position: relative;
	&:after {
		content: '';
		height: 3px;
		background-color: var(--bteal);
		position: absolute;
		left: 0;
		right: 0;
		bottom: -1px;
		transform: translateY(4px);
		transition: all 0.2s; 
		opacity: 1px;
	}
`
const ArrowDown = styled(ChevronDown)`
	width: 16px;
	color: var(--bteal60);
	transform: ${props => props.rotate ? 'rotate(180deg)' : 'rotate(0deg)'};
	transition: transform 0.25s;
`
const CategoryMenu = styled(FilterMenu)`
	margin-bottom: var(--s);
`
const TagMenu = styled(FilterMenu)`
`
const FilterButton = styled.button`
	margin-right: var(--s);
	margin-bottom: var(--s);
	position: relative;
	padding-bottom: 0;
	transition: all 0.2s; 
	box-sizing: border-box;
	&:after {
		content: '';
		height: 3px;
		background-color: var(--bteal);
		position: absolute;
		left: 0;
		right: 0;
		bottom: -1px;
		transform: translateY(4px);
		transition: all 0.2s; 
		opacity: ${props => props.underline ? '1' : '0'}
	}
	:hover {
		&:after {
			opacity: 1px;
		}
	}
	:last-of-type {
		margin-right: 0px;
	}
`
const AllCategories = styled(FilterButton)`
	margin-bottom: var(--s);
`
const CategoryButton = styled(FilterButton)`
`
const TagButton = styled(FilterButton)`
	color: var(--bteal);
	&:after {
		background-color: var(--dteal);
	}
	${mobile}{
		color: var(--white);
		&:after {
			background-color: var(--bteal);
		}
	}

`
const Posts = styled(Section)`
`
const NoPosts = styled.div`
	grid-column: span 12;
	margin-bottom: var(--xxxxl);
`
const Post = styled(Tile)`
	grid-column: span 4;
	margin-bottom: var(--xxxxl);
	${tablet}{
		grid-column: span 6;
	}
	${mobile}{
		grid-column: span 12;
	}
`
const StyledSlideDown = styled(SlideDown)`
	grid-column: span 12;
	overflow-x: hidden;
`
PostIndex.propTypes = {
	data: PropTypes.object,
}

export const query = graphql`
  query PostIndexQuery($slug: String ) {
    page: sanityPostIndex(slug: {current: {eq: $slug}}) {
      title
			_type
			slug {
				current
			}
			header {
				...pageHeader
			}
			categoryMenu {
				title
				slug {
					current
				}
			}
			tagMenu {
				title
				slug {
					current
				}
			}
      seo{
				...seo
			}
    }
  }
`

export default PostIndex